import { type ButtonHTMLAttributes } from 'react';
import { Icon } from '../Icon';
import type { IconName } from '../Icon';
import { cn } from '@/utils/cn';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'style'
> {
  /** The icon to render inside the button. */
  icon: IconName;
  /** Accessible label — required for screen readers since there is no visible text. */
  'aria-label': string;
}

/**
 * IconButton — Primer component.
 *
 * A circular ghost button that renders a single icon. No visible label —
 * `aria-label` is required for accessibility.
 *
 * > **Sealed component.** `className` and `style` are not accepted.
 * > Layout concerns (visibility, spacing, grid placement) belong on a wrapper
 * > `<div>` at the call site:
 * >
 * > ```tsx
 * > // Mobile-only back navigation
 * > <div className="md:hidden">
 * >   <IconButton icon="chevron-left" aria-label="Back to Settings" onClick={…} />
 * > </div>
 * > ```
 *
 * ### Appearance
 * - Shape  : `rounded-full` (circle)
 * - Fill   : `bg-surface`
 * - Border : `border border-default`
 * - Icon   : any valid `IconName`, rendered at `large` size
 * - Hover  : `hover:bg-interactive-quiet`
 *
 * @example
 * // Close button
 * <IconButton icon="x" aria-label="Close" onClick={onClose} />
 */
export function IconButton({ icon, 'aria-label': ariaLabel, ...props }: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(
        // Shape & size — w-fit prevents stretching inside flex/grid containers
        'inline-flex items-center justify-center rounded-full p-2 w-fit',
        // Colours
        'bg-surface border border-default text-body',
        // Hover
        'hover:bg-interactive-quiet',
        // Focus ring
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        'focus-visible:ring-(--border-color-interactiveloud)',
        // Cursor
        'cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
        // Transition
        'transition-colors duration-150'
      )}
      {...props}
    >
      <Icon name={icon} size="large" />
    </button>
  );
}

export default IconButton;
